import type {
  CreateHandoffRequest,
  HandoffDto,
  HandoffStatus,
  HandoffToRole,
  MedicalEscalationReplyRequest,
  MedicalEscalationReplyResponse,
  SupportEscalationThread,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { jsonOr401 } from "./clinician-api";

// TES-292 [J20] — coach↔doctor handoff / escalation client. Auth rides the
// httpOnly clinician session cookie (`withCredentials`), scoped by
// `tenantHeader()`. Create is patient-scoped; the queue + lifecycle
// actions are keyed by handoff id.

function commonHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", ...tenantHeader() };
}

export class HandoffApiError extends Error {}

async function jsonOrError<T>(res: Response): Promise<T> {
  if (res.status === 401 || res.status === 403) return jsonOr401<T>(res);
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: unknown; error?: unknown };
      const msg = body.message ?? body.error;
      if (typeof msg === "string" && msg.trim()) detail = msg;
      else if (Array.isArray(msg) && msg.length) detail = msg.join("; ");
    } catch {
      // keep status-code detail
    }
    throw new HandoffApiError(detail);
  }
  return (await res.json()) as T;
}

const BASE = `${API_BASE}/employee-api/v1/handoffs`;

// The caller's (or an overridden) role queue. Open handoffs by default; pass a
// status to narrow.
export async function listHandoffQueue(
  toRole?: HandoffToRole,
  status?: HandoffStatus,
): Promise<HandoffDto[]> {
  const params = new URLSearchParams();
  if (toRole) params.set("toRole", toRole);
  if (status) params.set("status", status);
  const qs = params.toString();
  const res = await apiFetch(`${BASE}${qs ? `?${qs}` : ""}`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function acknowledgeHandoff(id: string): Promise<HandoffDto> {
  const res = await apiFetch(`${BASE}/${id}/acknowledge`, {
    method: "POST",
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function startHandoff(id: string): Promise<HandoffDto> {
  const res = await apiFetch(`${BASE}/${id}/start`, {
    method: "POST",
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function resolveHandoff(
  id: string,
  resolution?: string,
): Promise<HandoffDto> {
  const res = await apiFetch(`${BASE}/${id}/resolve`, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(resolution ? { resolution } : {}),
    ...withCredentials,
  });
  return jsonOrError(res);
}

// TES-651 — ONE patient's handoffs, regardless of the addressed role.
//
// Replaces `listHandoffQueue("doctor")` in the chat escalation row: it fetched
// the whole doctor queue and filtered out one row in the browser, which gave a
// coach every patient's escalation texts decrypted. Since TES-651 `?toRole` may
// only confirm the caller's own role, so that path no longer exists.
export async function listPatientHandoffs(
  patientId: string,
): Promise<HandoffDto[]> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/handoffs`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOrError(res);
}

// TES-1119 (Ö-333) — the coach's standard reply after her own "Skicka till
// läkare", posted as her in the coach thread. Names the handoff it follows,
// so the server can refuse it without an escalation; idempotent per handoff.
export async function postMedicalEscalationReply(
  patientId: string,
  body: MedicalEscalationReplyRequest,
): Promise<MedicalEscalationReplyResponse> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${encodeURIComponent(
      patientId,
    )}/medical-escalation-reply`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOrError(res);
}

// TES-1110 — the patient's Ella Support thread behind a support escalation
// addressed to the caller's role (doctor or coach). Patient-scoped, so the
// read is logged like any other patient read.
export async function fetchSupportEscalationThread(
  patientId: string,
  handoffId: string,
): Promise<SupportEscalationThread> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${encodeURIComponent(
      patientId,
    )}/support-escalations/${encodeURIComponent(handoffId)}/messages`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOrError(res);
}

// Originate a handoff for a patient (from the caller's role, server-derived).
export async function createHandoff(
  patientId: string,
  body: CreateHandoffRequest,
): Promise<HandoffDto> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/patients/${patientId}/handoffs`,
    {
      method: "POST",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOrError(res);
}
